import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import api, { TIMEOUT_EXTRACAO_IA } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  FileDown, FileText, DollarSign, Clock, AlertCircle, TrendingUp, Lock, CheckCircle2, Search, Calendar,
  RefreshCw, ChevronLeft, ChevronRight, X, Building2, Upload, ChevronDown, ChevronUp, Trash2, AlertTriangle, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import EmptyState from '../components/EmptyState';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';

const fmt = (v) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);
const fmtDate = (d) => { if (!d) return '---'; return new Date(d + 'T00:00:00').toLocaleDateString('pt-BR'); };
const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

const STATUS_CFG = {
  Pago:     { cls: 'bg-emerald-100 text-emerald-700 border-emerald-300', dot: 'bg-emerald-500', label: 'Pago' },
  Pendente: { cls: 'bg-blue-100 text-blue-700 border-blue-300',         dot: 'bg-blue-500',    label: 'Pendente' },
  Atrasado: { cls: 'bg-red-100 text-red-700 border-red-300',            dot: 'bg-red-500',     label: 'Atrasado' },
};

// ============================================================
// CONFERÊNCIA COM FÁBRICA — wizard de upload/revisão/conferência/baixa
// ============================================================
const MEDIA_TYPES_ACEITOS = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'];
const TAMANHO_MAX_ARQUIVO_MB = 8;
const TAMANHO_MAX_ARQUIVO = TAMANHO_MAX_ARQUIVO_MB * 1024 * 1024;

const inpSm = 'w-full border border-slate-200 rounded-lg bg-white px-3 h-9 text-xs font-semibold text-slate-800 outline-none focus:border-[#0A3D73] transition-all';
const lblSm = 'text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block';

// Espelham normalizar_parcela()/converter_valor_brasileiro() do backend (services/conferencia_comissao.py)
// para validar/normalizar ANTES do envio, em vez de depender só do backend para pegar erro.
function normalizarParcelaFrontend(raw) {
  if (raw === null || raw === undefined) return null;
  let texto = String(raw).trim();
  if (!texto) return null;
  if (texto.includes('/')) texto = texto.split('/')[0].trim();
  if (!/^\d+$/.test(texto)) return null;
  const n = parseInt(texto, 10);
  return n > 0 ? n : null;
}

function converterValorBrasileiroFrontend(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null;
  let texto = String(raw).trim().replace('R$', '').trim();
  if (!texto) return null;
  const negativo = texto.startsWith('-');
  if (negativo) texto = texto.slice(1).trim();
  if (!texto) return null;
  if (texto.includes(',')) texto = texto.replace(/\./g, '').replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(texto)) return null;
  const valor = parseFloat(texto);
  return negativo ? -valor : valor;
}

// Em requisições com responseType:'blob', erros HTTP também chegam como Blob (não JSON) —
// precisa ler o texto do blob e fazer o parse antes de conseguir a mensagem do backend.
async function extrairErroBlob(err) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const texto = await data.text();
      return JSON.parse(texto)?.detail || null;
    } catch {
      return null;
    }
  }
  return err?.response?.data?.detail || null;
}

function BlocoResultado({ titulo, Icone, cor, itens, colunas, aberto, onToggle, vazio }) {
  return (
    <div className={`rounded-xl border overflow-hidden ${cor.borda} ${cor.fundo}`}>
      <button type="button" onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left">
        <div className="flex items-center gap-2">
          <Icone size={14} className={cor.icone} />
          <span className={`text-xs font-black uppercase tracking-wide ${cor.texto}`}>{titulo}</span>
          <span className={`text-xs font-black px-2 py-0.5 rounded-full ${cor.badge}`}>{itens.length}</span>
        </div>
        {aberto ? <ChevronUp size={14} className={cor.icone} /> : <ChevronDown size={14} className={cor.icone} />}
      </button>
      {aberto && (
        itens.length > 0 ? (
          <div className="border-t border-white/60 overflow-x-auto bg-white/50">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left">
                  {colunas.map(c => (
                    <th key={c.key} className="px-3 py-2 font-black text-slate-500 uppercase tracking-wide whitespace-nowrap">{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {itens.map((it, i) => (
                  <tr key={i} className="border-t border-slate-200/70">
                    {colunas.map(c => (
                      <td key={c.key} className="px-3 py-2 whitespace-nowrap">{c.render ? c.render(it) : (it[c.key] ?? '—')}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="px-4 pb-3 text-xs font-semibold text-slate-400">{vazio}</p>
        )
      )}
    </div>
  );
}

const Comissoes = () => {
  const { isAdmin } = useAuth();
  const location = useLocation();
  const [vencimentos, setVencimentos] = useState([]);
  const [notas, setNotas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('TODOS');
  const [selectedMonth, setSelectedMonth] = useState('TODOS');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [filterRepresentada, setFilterRepresentada] = useState('TODAS');
  const selectAllRef = useRef(null);

  // ── Wizard: Conferência com Fábrica ──────────────────────────────────────
  const [conferenciaDialog, setConferenciaDialog] = useState(false);
  const [conferenciaStep, setConferenciaStep] = useState('upload'); // 'upload' | 'revisao' | 'conferencia' | 'resultado'
  const [arquivoNomeSelecionado, setArquivoNomeSelecionado] = useState('');
  const [extracaoDados, setExtracaoDados] = useState(null); // { arquivo_hash, arquivo_nome } — vindos da resposta do backend
  const [avisosExtracao, setAvisosExtracao] = useState([]);
  const [itensRevisao, setItensRevisao] = useState([]); // [{cliente, numero_nf, parcela, valor_comissao}]
  const [periodoInicio, setPeriodoInicio] = useState('');
  const [periodoFim, setPeriodoFim] = useState('');
  const [conferenciaResultado, setConferenciaResultado] = useState(null);
  const [baixaResultado, setBaixaResultado] = useState(null);
  const [loadingExtracao, setLoadingExtracao] = useState(false);
  const [loadingConferencia, setLoadingConferencia] = useState(false);
  const [loadingBaixa, setLoadingBaixa] = useState(false);
  const [loadingExportConferenciaExcel, setLoadingExportConferenciaExcel] = useState(false);
  const [erroWizard, setErroWizard] = useState('');
  const [blocosAbertos, setBlocosAbertos] = useState({ elegiveis: true, divergencias: true, naoRepflow: true, naoRelatorio: true });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const statusParam = params.get('status');
    if (statusParam && ['Pendente', 'Atrasado', 'Pago'].includes(statusParam)) {
      setFilterStatus(statusParam);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { setCurrentPage(1); setSelectedIds(new Set()); }, [filterStatus, selectedMonth, searchTerm, filterRepresentada]);

  const fetchData = async () => {
    try {
      const [vencRes, notasRes, clientesRes] = await Promise.all([api.get('/vencimentos'), api.get('/notas-fiscais'), api.get('/clientes')]);
      setVencimentos(vencRes.data || []);
      setNotas(notasRes.data || []);
      setClientes(clientesRes.data || []);
    } catch { toast.error('Erro ao carregar comissões'); }
    finally { setLoading(false); }
  };

  const handleMarcarPago = async (vencId) => {
    if (!isAdmin) return;
    try {
      await api.put(`/vencimentos/${vencId}`, { status: 'Pago', data_pagamento: new Date().toISOString().split('T')[0] });
      toast.success('Pagamento confirmado!');
      fetchData();
    } catch { toast.error('Erro ao confirmar pagamento'); }
  };

  const handleExportPdf = async () => {
    if (!isAdmin) return toast.error('Acesso restrito');
    setLoadingPdf(true);
    try {
      const r = await api.post('/export/comissoes-pdf',
        { status: filterStatus, mes: selectedMonth, search: searchTerm, representada_nome: filterRepresentada === 'TODAS' ? '' : filterRepresentada },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comissoes_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('PDF gerado!');
    } catch { toast.error('Erro ao gerar PDF'); }
    finally { setLoadingPdf(false); }
  };

  const handleRecalcularComissoes = async () => {
    if (!isAdmin) return;
    try {
      const r = await api.post('/admin/recalcular-comissoes');
      toast.success(`${r.data.corrigidos} comissões recalculadas!`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao recalcular comissões');
    }
  };

  const handleExportExcel = async () => {
    if (!isAdmin) return toast.error('Acesso restrito');
    try {
      const r = await api.get('/export/comissoes', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `comissoes_${new Date().toLocaleDateString('pt-BR').replace(/\//g,'-')}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      toast.success('Excel gerado!');
    } catch { toast.error('Erro ao gerar Excel'); }
  };

  // ── Wizard: Conferência com Fábrica ──────────────────────────────────────
  const resetConferenciaWizard = () => {
    setConferenciaStep('upload');
    setArquivoNomeSelecionado('');
    setExtracaoDados(null);
    setAvisosExtracao([]);
    setItensRevisao([]);
    setPeriodoInicio('');
    setPeriodoFim('');
    setConferenciaResultado(null);
    setBaixaResultado(null);
    setLoadingExtracao(false);
    setLoadingConferencia(false);
    setLoadingBaixa(false);
    setLoadingExportConferenciaExcel(false);
    setErroWizard('');
    setBlocosAbertos({ elegiveis: true, divergencias: true, naoRepflow: true, naoRelatorio: true });
  };

  const handleAbrirConferenciaFabrica = () => {
    resetConferenciaWizard();
    setConferenciaDialog(true);
  };

  const wizardEmAndamento = loadingExtracao || loadingConferencia || loadingBaixa || loadingExportConferenciaExcel;

  const handleFecharConferenciaWizard = () => {
    if (wizardEmAndamento) return; // não fecha durante uma requisição em curso
    setConferenciaDialog(false);
    resetConferenciaWizard();
  };

  const handleArquivoSelecionado = (file) => {
    if (!file || loadingExtracao) return;
    setErroWizard('');

    if (!MEDIA_TYPES_ACEITOS.includes(file.type)) {
      setErroWizard('Formato não suportado. Envie um PDF ou imagem (PNG/JPG).');
      return;
    }
    if (file.size === 0) {
      setErroWizard('O arquivo selecionado está vazio.');
      return;
    }
    if (file.size > TAMANHO_MAX_ARQUIVO) {
      setErroWizard(`Arquivo muito grande — máximo de ${TAMANHO_MAX_ARQUIVO_MB} MB.`);
      return;
    }

    setArquivoNomeSelecionado(file.name);
    const reader = new FileReader();
    reader.onerror = () => setErroWizard('Erro ao ler o arquivo selecionado.');
    reader.onload = async (e) => {
      const dataUrl = e.target.result;
      const [header, b64] = dataUrl.split(',');
      const mediaType = header.match(/:(.*?);/)?.[1] || file.type;

      setLoadingExtracao(true);
      try {
        const resp = await api.post('/comissoes/extrair-relatorio-fabrica', {
          arquivo_b64: b64,
          media_type: mediaType,
          arquivo_nome: file.name,
        }, { timeout: TIMEOUT_EXTRACAO_IA });
        const dados = resp.data;
        // arquivo_hash/arquivo_nome usados nas próximas chamadas vêm da resposta do backend, não do File local
        setExtracaoDados({ arquivo_hash: dados.arquivo_hash, arquivo_nome: dados.arquivo_nome });
        // parcela já normalizada na carga (ex: "1/3" -> 1) para edição sempre como inteiro
        setItensRevisao((dados.itens || []).map(it => ({ ...it, parcela: normalizarParcelaFrontend(it.parcela) ?? '' })));
        setPeriodoInicio(dados.periodo_inicio || '');
        setPeriodoFim(dados.periodo_fim || '');
        setAvisosExtracao(dados.avisos || []);
        setConferenciaStep('revisao');
      } catch (err) {
        setErroWizard(err.response?.data?.detail || 'Erro ao extrair dados do relatório.');
      } finally {
        setLoadingExtracao(false);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleEditarItemRevisao = (index, campo, valor) => {
    setItensRevisao(prev => prev.map((it, i) => i === index ? { ...it, [campo]: valor } : it));
  };

  const handleRemoverItemRevisao = (index) => {
    setItensRevisao(prev => prev.filter((_, i) => i !== index));
  };

  // Valida e normaliza tudo ANTES do envio: zero itens, período obrigatório/coerente,
  // NF como texto (sem mexer em zeros à esquerda), parcela inteira > 0, valor válido e não-negativo.
  const validarENormalizarRevisao = () => {
    if (itensRevisao.length === 0) {
      return { ok: false, erro: 'Não há itens para conferir — todos foram removidos ou nenhum foi extraído.' };
    }
    if (!periodoInicio || !periodoFim) {
      return { ok: false, erro: 'Informe o período (início e fim) antes de conferir.' };
    }
    if (periodoInicio > periodoFim) {
      return { ok: false, erro: 'O período de início não pode ser posterior ao período de fim.' };
    }

    const itensNormalizados = [];
    for (let i = 0; i < itensRevisao.length; i++) {
      const it = itensRevisao[i];
      const numeroNf = String(it.numero_nf || '').trim(); // texto puro — nenhuma remoção de zeros aqui
      if (!numeroNf) {
        return { ok: false, erro: `Linha ${i + 1}: número da NF é obrigatório.` };
      }
      const parcela = normalizarParcelaFrontend(it.parcela);
      if (parcela === null) {
        return { ok: false, erro: `Linha ${i + 1} (NF ${numeroNf}): parcela deve ser um número inteiro maior que zero.` };
      }
      const valorComissao = converterValorBrasileiroFrontend(it.valor_comissao);
      if (valorComissao === null || valorComissao < 0) {
        return { ok: false, erro: `Linha ${i + 1} (NF ${numeroNf}): valor da comissão inválido ou negativo.` };
      }
      itensNormalizados.push({ cliente: it.cliente || '', numero_nf: numeroNf, parcela, valor_comissao: valorComissao });
    }
    return { ok: true, itensNormalizados };
  };

  const handleConferirFabrica = async () => {
    if (loadingConferencia) return; // impede duplo clique/requisição duplicada
    const validacao = validarENormalizarRevisao();
    if (!validacao.ok) {
      setErroWizard(validacao.erro);
      return;
    }
    setErroWizard('');
    setLoadingConferencia(true);
    try {
      const resp = await api.post('/comissoes/conferir-fabrica', {
        periodo_inicio: periodoInicio,
        periodo_fim: periodoFim,
        itens: validacao.itensNormalizados,
        arquivo_hash: extracaoDados?.arquivo_hash,
        arquivo_nome: extracaoDados?.arquivo_nome,
      });
      setConferenciaResultado(resp.data);
      // itensRevisao passa a refletir os valores normalizados/validados, já que
      // handleConfirmarBaixa reenvia este mesmo array para /baixar-fabrica
      setItensRevisao(validacao.itensNormalizados);
      setConferenciaStep('conferencia');
    } catch (err) {
      setErroWizard(err.response?.data?.detail || 'Erro ao conferir com o RepFlow.');
    } finally {
      setLoadingConferencia(false);
    }
  };

  const handleExportarConferenciaExcel = async () => {
    if (loadingExportConferenciaExcel) return; // impede clique duplo/requisição duplicada
    setErroWizard('');
    setLoadingExportConferenciaExcel(true);
    try {
      const resp = await api.post('/comissoes/exportar-conferencia-excel', {
        periodo_inicio: periodoInicio,
        periodo_fim: periodoFim,
        itens: itensRevisao,
        arquivo_hash: extracaoDados?.arquivo_hash,
        arquivo_nome: extracaoDados?.arquivo_nome,
      }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `conferencia_comissao_${new Date().toLocaleDateString('pt-BR').replace(/\//g, '-')}.xlsx`);
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Excel da conferência gerado!');
    } catch (err) {
      const mensagem = await extrairErroBlob(err);
      setErroWizard(mensagem || 'Erro ao gerar Excel da conferência.');
    } finally {
      setLoadingExportConferenciaExcel(false);
    }
  };

  const handleConfirmarBaixa = async () => {
    if (loadingBaixa) return; // impede baixa dupla/simultânea
    const qtdElegiveis = conferenciaResultado?.elegiveis_baixa?.length || 0;
    if (qtdElegiveis === 0) return;
    setErroWizard('');
    setLoadingBaixa(true);
    try {
      const resp = await api.post('/comissoes/baixar-fabrica', {
        periodo_inicio: periodoInicio,
        periodo_fim: periodoFim,
        itens: itensRevisao,
        arquivo_hash: extracaoDados?.arquivo_hash,
        arquivo_nome: extracaoDados?.arquivo_nome,
      });
      setBaixaResultado(resp.data);
      setConferenciaStep('resultado');
      fetchData(); // refetch imediato após sucesso — nunca em caso de erro
    } catch (err) {
      setErroWizard(err.response?.data?.detail || 'Erro ao confirmar a baixa.');
      // permanece na etapa de conferência, sem fechar o modal e sem refetch
    } finally {
      setLoadingBaixa(false);
    }
  };

  const toggleBloco = (chave) => setBlocosAbertos(prev => ({ ...prev, [chave]: !prev[chave] }));

  const clienteIdMapC = useMemo(() => Object.fromEntries(clientes.map(c => [c.id, c])), [clientes]);

  const vencimentosEnriquecidos = useMemo(() =>
    vencimentos.map(v => {
      const nota = notas.find(n => n.id === v.nota_fiscal_id);
      const clienteId = nota?.cliente_id;
      const nomeBase = v.cliente_nome || nota?.cliente_nome || 'N/A';
      const cliente_nome = clienteIdMapC[clienteId]?.nome_fantasia || nomeBase;
      return { ...v, nota, cliente_nome, numero_nf: v.numero_nf || nota?.numero_nf, representada_nome: nota?.representada_nome || '' };
    }), [vencimentos, notas, clienteIdMapC]
  );

  const representadas = useMemo(() => {
    const set = new Set(vencimentosEnriquecidos.map(v => v.representada_nome).filter(Boolean));
    return Array.from(set).sort();
  }, [vencimentosEnriquecidos]);

  const vencimentosFiltrados = useMemo(() => {
    return vencimentosEnriquecidos.filter(v => {
      const matchStatus = filterStatus === 'TODOS' || v.status === filterStatus;
      const matchMes = selectedMonth === 'TODOS' || (v.data_vencimento && (new Date(v.data_vencimento + 'T00:00:00').getMonth() + 1).toString() === selectedMonth);
      const s = searchTerm.toLowerCase();
      const matchSearch = !s || v.numero_nf?.toLowerCase().includes(s) || v.cliente_nome?.toLowerCase().includes(s);
      const matchRepresentada = filterRepresentada === 'TODAS' || v.representada_nome === filterRepresentada;
      return matchStatus && matchMes && matchSearch && matchRepresentada;
    });
  }, [vencimentosEnriquecidos, filterStatus, selectedMonth, searchTerm, filterRepresentada]);

  // Base para os cards de totais (Total Comissões/Recebido/A Receber/Atrasado).
  // Aplica representada/mês/busca, mas OMITE matchStatus DE PROPÓSITO — os cards
  // devem sempre mostrar a composição pago/pendente/atrasado do período/representada
  // selecionados, mesmo com uma aba de status (Pendente/Atrasado/Pago) ativa na tabela
  // abaixo. Se algum dia decidirem que os cards também devem reagir ao status, é
  // decisão de produto — não um bug de filtro esquecido aqui.
  const vencimentosParaCards = useMemo(() => {
    return vencimentosEnriquecidos.filter(v => {
      const matchMes = selectedMonth === 'TODOS' || (v.data_vencimento && (new Date(v.data_vencimento + 'T00:00:00').getMonth() + 1).toString() === selectedMonth);
      const s = searchTerm.toLowerCase();
      const matchSearch = !s || v.numero_nf?.toLowerCase().includes(s) || v.cliente_nome?.toLowerCase().includes(s);
      const matchRepresentada = filterRepresentada === 'TODAS' || v.representada_nome === filterRepresentada;
      return matchMes && matchSearch && matchRepresentada;
    });
  }, [vencimentosEnriquecidos, selectedMonth, searchTerm, filterRepresentada]);

  const totalPages = Math.ceil(vencimentosFiltrados.length / itemsPerPage);
  const vencimentosPagina = vencimentosFiltrados.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const selecionaveisPagina = vencimentosPagina.filter(v => v.status !== 'Pago').map(v => v.id);
  const allSelected = selecionaveisPagina.length > 0 && selecionaveisPagina.every(id => selectedIds.has(id));
  const someSelected = selecionaveisPagina.some(id => selectedIds.has(id)) && !allSelected;

  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = someSelected;
  }, [someSelected]);

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(prev => { const n = new Set(prev); selecionaveisPagina.forEach(id => n.delete(id)); return n; });
    } else {
      setSelectedIds(prev => { const n = new Set(prev); selecionaveisPagina.forEach(id => n.add(id)); return n; });
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const handleBulkPagar = async () => {
    try {
      const { data } = await api.post('/vencimentos/bulk-pagar', { ids: Array.from(selectedIds) });
      toast.success(`${data.atualizados} vencimento(s) marcado(s) como Pago!`);
      setSelectedIds(new Set());
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao executar ação em massa');
    }
  };

  const totalPago     = vencimentosParaCards.filter(v => v.status === 'Pago').reduce((a,v) => a+(v.comissao_calculada||0), 0);
  const totalPendente = vencimentosParaCards.filter(v => v.status === 'Pendente').reduce((a,v) => a+(v.comissao_calculada||0), 0);
  const totalAtrasado = vencimentosParaCards.filter(v => v.status === 'Atrasado').reduce((a,v) => a+(v.comissao_calculada||0), 0);
  const totalGeral    = totalPago + totalPendente + totalAtrasado;

  const contadores = {
    TODOS: vencimentos.length,
    Pendente: vencimentos.filter(v => v.status === 'Pendente').length,
    Atrasado: vencimentos.filter(v => v.status === 'Atrasado').length,
    Pago:     vencimentos.filter(v => v.status === 'Pago').length,
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin"/>
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Comissões...</p>
    </div>
  );

  return (
    <div className="p-4 md:p-6 bg-[#E9EEF2] min-h-screen">

      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-black text-slate-900 flex items-center gap-2">
            Comissões {!isAdmin && <Lock size={14} className="text-slate-400"/>}
          </h1>
          <p className="text-xs text-slate-500 font-semibold mt-0.5">Consolidado Financeiro · {vencimentos.length} vencimentos</p>
        </div>
        <div className="flex flex-wrap gap-2 items-center">
          <div className="flex items-center bg-white border border-slate-200 rounded-lg h-9 px-3 gap-2 shadow-sm">
            <Search size={13} className="text-slate-400"/>
            <input placeholder="NF, cliente..." className="text-xs font-semibold bg-transparent outline-none w-36 text-slate-700"
              value={searchTerm} onChange={e => setSearchTerm(e.target.value)}/>
          </div>
          {isAdmin && (
            <button onClick={handleRecalcularComissoes}
              className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors">
              <RefreshCw size={13}/> Recalcular Comissões
            </button>
          )}
          {isAdmin && (
            <button onClick={handleExportExcel}
              className="flex items-center gap-2 bg-[#166534] hover:bg-[#14532D] text-white text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors">
              <FileDown size={13}/> Exportar Excel
            </button>
          )}
          {isAdmin && (
            <button onClick={handleExportPdf} disabled={loadingPdf}
              className="flex items-center gap-2 bg-red-700 hover:bg-red-800 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors">
              <FileText size={13}/> {loadingPdf ? 'Gerando...' : 'Exportar PDF'}
            </button>
          )}
          {isAdmin && (
            <button onClick={handleAbrirConferenciaFabrica}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-black uppercase px-4 h-9 rounded-lg shadow-sm transition-colors">
              <Upload size={13}/> Conferir Relatório da Fábrica
            </button>
          )}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        {[
          {label:'Total Comissões',  value:`R$ ${fmt(totalGeral)}`,    icon:DollarSign,   cor:'#374151', fundo:'#F9FAFB'},
          {label:'Recebido (Pago)',  value:`R$ ${fmt(totalPago)}`,     icon:CheckCircle2, cor:'#166534', fundo:'#DCFCE7'},
          {label:'A Receber',        value:`R$ ${fmt(totalPendente)}`, icon:Clock,        cor:'#1E40AF', fundo:'#EFF6FF'},
          {label:'Atrasado',         value:`R$ ${fmt(totalAtrasado)}`, icon:AlertCircle,  cor: totalAtrasado>0?'#991B1B':'#374151', fundo: totalAtrasado>0?'#FEE2E2':'#F9FAFB'},
        ].map((k,i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{k.label}</p>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{background:k.fundo}}>
                <k.icon size={13} style={{color:k.cor}}/>
              </div>
            </div>
            <p className="text-lg font-black font-mono" style={{color:k.cor}}>{k.value}</p>
          </div>
        ))}
      </div>

      {/* FILTROS */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="flex gap-2 flex-wrap">
          {[{k:'TODOS',label:'Todos'},{k:'Pendente',label:'Pendente'},{k:'Atrasado',label:'Atrasado'},{k:'Pago',label:'Pago'}].map(s => (
            <button key={s.k} onClick={() => setFilterStatus(s.k)}
              className={`px-3 py-1.5 text-xs font-bold rounded-lg border transition-all ${filterStatus===s.k?'bg-[#0A3D73] text-white border-[#0A3D73] shadow-sm':'bg-white text-slate-600 border-slate-200 hover:border-[#0A3D73]'}`}>
              {s.label} <span className="opacity-60">({contadores[s.k]||0})</span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 h-9 shadow-sm">
          <Calendar size={12} className="text-slate-400"/>
          <select value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)}
            className="text-xs font-bold bg-transparent outline-none cursor-pointer text-slate-700">
            <option value="TODOS">Todos os meses</option>
            {MESES.map((m,i) => <option key={m} value={(i+1).toString()}>{m}</option>)}
          </select>
        </div>
        {representadas.length > 0 && (
          <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 h-9 shadow-sm">
            <Building2 size={12} className="text-slate-400"/>
            <select value={filterRepresentada} onChange={e => setFilterRepresentada(e.target.value)}
              className="text-xs font-bold bg-transparent outline-none cursor-pointer text-slate-700">
              <option value="TODAS">Todas as representadas</option>
              {representadas.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* BULK ACTION BAR */}
      {isAdmin && selectedIds.size > 0 && (
        <div className="flex items-center justify-between bg-[#0A3D73] text-white rounded-xl px-5 py-3 mb-4 shadow-sm">
          <span className="text-sm font-black">{selectedIds.size} vencimento(s) selecionado(s)</span>
          <div className="flex gap-2">
            <button onClick={handleBulkPagar}
              className="flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white text-xs font-black uppercase px-4 h-8 rounded-lg transition-colors">
              <CheckCircle2 size={12}/> Marcar como Pago
            </button>
            <button onClick={() => setSelectedIds(new Set())}
              className="flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white text-xs font-black uppercase px-4 h-8 rounded-lg transition-colors">
              <X size={12}/> Cancelar seleção
            </button>
          </div>
        </div>
      )}

      {/* TABELA */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp size={15} className="text-[#0A3D73]"/>
            <h3 className="text-xs font-black uppercase tracking-wide text-slate-700">Fluxo de Comissões</h3>
          </div>
          <span className="text-xs font-bold text-slate-400">{vencimentosFiltrados.length} registros</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-left">
            <thead>
              <tr className="bg-[#0A3D73] text-white text-xs font-black uppercase tracking-wide">
                {isAdmin && (
                  <th className="px-4 py-3 w-10 text-center">
                    <input
                      type="checkbox"
                      ref={selectAllRef}
                      checked={allSelected}
                      onChange={handleSelectAll}
                      disabled={selecionaveisPagina.length === 0}
                      className="w-3.5 h-3.5 accent-white cursor-pointer disabled:cursor-not-allowed disabled:opacity-40"
                    />
                  </th>
                )}
                <th className="px-4 py-3">NF</th>
                <th className="px-4 py-3">Cliente</th>
                <th className="px-4 py-3 text-center">Parcela</th>
                <th className="px-4 py-3">Vencimento</th>
                <th className="px-4 py-3">Dt. Faturamento</th>
                <th className="px-4 py-3 text-right">Comissão R$</th>
                <th className="px-4 py-3 text-center">Status</th>
                <th className="px-4 py-3">Data Pgto</th>
                {isAdmin && <th className="px-4 py-3 text-center">Ação</th>}
              </tr>
            </thead>
            <tbody>
              {vencimentosFiltrados.length === 0 ? (
                <tr><td colSpan={isAdmin?10:8}>
                  <EmptyState
                    icon={
                      <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
                        <circle cx="36" cy="38" r="24" stroke="#4A90D9" strokeWidth="2"/>
                        <circle cx="36" cy="38" r="17" stroke="#7BB8F0" strokeWidth="1.5" strokeDasharray="4 2"/>
                        <path d="M36 24v4M36 48v4" stroke="#4A90D9" strokeWidth="2" strokeLinecap="round"/>
                        <path d="M30 30c0-3.3 2.7-6 6-6s6 2.7 6 6-2.7 5-6 5-6 1.7-6 5 2.7 6 6 6 6-2.7 6-6" stroke="#4A90D9" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    }
                    title="Nenhum vencimento encontrado"
                    subtitle="Os vencimentos aparecem após emitir notas fiscais"
                  />
                </td></tr>
              ) : vencimentosPagina.map((v, idx) => {
                const cfg = STATUS_CFG[v.status] || STATUS_CFG.Pendente;
                return (
                  <tr key={v.id}
                    className={`text-xs border-b border-slate-100 transition-colors hover:bg-blue-50
                      ${idx%2===0?'bg-white':'bg-slate-50/60'}
                      ${v.status==='Atrasado'?'border-l-2 border-l-red-400':''}
                      ${selectedIds.has(v.id)?'bg-blue-50/80':''}`}>
                    {isAdmin && (
                      <td className="px-4 py-2.5 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(v.id)}
                          onChange={() => handleSelectOne(v.id)}
                          disabled={v.status === 'Pago'}
                          className="w-3.5 h-3.5 accent-[#0A3D73] cursor-pointer disabled:cursor-not-allowed disabled:opacity-30"
                        />
                      </td>
                    )}
                    <td className="px-4 py-2.5 font-mono font-black text-[#0A3D73] whitespace-nowrap">NF {v.numero_nf}</td>
                    <td className="px-4 py-2.5 font-bold uppercase max-w-[160px] truncate">{v.cliente_nome}</td>
                    <td className="px-4 py-2.5 text-center font-black text-slate-500">{v.parcela}ª/{v.total_parcelas}</td>
                    <td className="px-4 py-2.5 font-bold whitespace-nowrap">{fmtDate(v.data_vencimento)}</td>
                    <td className="px-4 py-2.5 font-bold whitespace-nowrap text-slate-500">{fmtDate(v.nota?.data_emissao)}</td>
                    <td className="px-4 py-2.5 text-right font-black text-emerald-700 whitespace-nowrap">R$ {fmt(v.comissao_calculada)}</td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-black border rounded-full ${cfg.cls}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`}/>
                        {cfg.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-xs font-bold text-slate-400">{v.data_pagamento ? fmtDate(v.data_pagamento) : '---'}</td>
                    {isAdmin && (
                      <td className="px-4 py-2.5 text-center">
                        {v.status !== 'Pago' ? (
                          <button onClick={() => handleMarcarPago(v.id)}
                            className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black uppercase px-3 py-1.5 rounded-lg transition-colors mx-auto">
                            <CheckCircle2 size={10}/> Confirmar
                          </button>
                        ) : (
                          <CheckCircle2 size={16} className="text-emerald-500 mx-auto"/>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
            {vencimentosFiltrados.length > 0 && (
              <tfoot>
                <tr className="bg-[#0A3D73] text-white text-xs font-black">
                  <td colSpan={isAdmin?6:5} className="px-4 py-2.5 text-right uppercase tracking-wide">Total ({vencimentosFiltrados.length} venc.)</td>
                  <td className="px-4 py-2.5 text-right font-mono">R$ {fmt(vencimentosFiltrados.reduce((a,v) => a+(v.comissao_calculada||0), 0))}</td>
                  <td colSpan={isAdmin?3:2}/>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* ── PAGINAÇÃO ── */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-100 bg-white">
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-slate-500">Exibir:</span>
              <select
                value={itemsPerPage}
                onChange={e => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }}
                className="border border-slate-200 rounded-lg text-xs font-bold bg-white px-2 h-8 outline-none focus:border-[#0A3D73] cursor-pointer">
                {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors">
                <ChevronLeft size={14}/>
              </button>
              <span className="text-xs font-black text-slate-600">
                Página {currentPage} de {totalPages} · {vencimentosFiltrados.length} registros
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A3D73] text-white disabled:opacity-30 disabled:cursor-not-allowed hover:bg-[#082D54] transition-colors">
                <ChevronRight size={14}/>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── DIALOG CONFERÊNCIA COM FÁBRICA ── */}
      {conferenciaDialog && (
        <Dialog open={conferenciaDialog} onOpenChange={(o) => { if (!o) handleFecharConferenciaWizard(); }}>
          <DialogContent className="max-w-4xl bg-white rounded-2xl p-0 overflow-hidden border-none shadow-2xl max-h-[90vh] flex flex-col">
            <DialogHeader className="p-5 bg-[#0A3D73] text-white flex-shrink-0">
              <DialogTitle className="text-xs uppercase tracking-widest flex items-center gap-2 font-black">
                <Upload size={15}/>
                {conferenciaStep === 'upload' && 'Conferir Relatório da Fábrica'}
                {conferenciaStep === 'revisao' && `Revisão dos Dados Extraídos — ${itensRevisao.length} linha(s)`}
                {conferenciaStep === 'conferencia' && 'Resultado da Conferência com o RepFlow'}
                {conferenciaStep === 'resultado' && 'Baixa Confirmada'}
              </DialogTitle>
            </DialogHeader>

            <div className="overflow-y-auto flex-1 min-h-0">
              {/* ── STEP UPLOAD ── */}
              {conferenciaStep === 'upload' && (
                <div className="p-6 space-y-5">
                  <p className="text-xs font-semibold text-slate-500">
                    Faça upload do relatório de comissões da fábrica (PDF ou imagem). A IA vai extrair os dados da tabela automaticamente.
                  </p>
                  {erroWizard && (
                    <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs font-bold text-red-700 flex items-center gap-2">
                      <XCircle size={14}/> {erroWizard}
                    </div>
                  )}
                  {loadingExtracao ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin"/>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Analisando relatório com IA...</p>
                      {arquivoNomeSelecionado && <p className="text-xs text-slate-400 font-semibold">{arquivoNomeSelecionado}</p>}
                    </div>
                  ) : (
                    <label className="block cursor-pointer">
                      <div className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-[#0A3D73] hover:bg-blue-50 transition-all">
                        <Upload size={32} className="mx-auto text-slate-300 mb-3"/>
                        <p className="text-sm font-black text-slate-600">Clique para selecionar ou arraste o arquivo</p>
                        <p className="text-xs text-slate-400 font-semibold mt-1">PDF, PNG ou JPG — máx. {TAMANHO_MAX_ARQUIVO_MB} MB</p>
                        {arquivoNomeSelecionado && (
                          <p className="text-xs text-[#0A3D73] font-black mt-3">Selecionado: {arquivoNomeSelecionado}</p>
                        )}
                      </div>
                      <input
                        type="file"
                        accept={MEDIA_TYPES_ACEITOS.join(',')}
                        className="hidden"
                        onChange={(e) => handleArquivoSelecionado(e.target.files?.[0])}
                      />
                    </label>
                  )}
                  <div className="flex justify-end pt-2 border-t border-slate-100">
                    <Button type="button" onClick={handleFecharConferenciaWizard} disabled={wizardEmAndamento}
                      className="rounded-xl text-xs font-black uppercase h-9 px-5 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-60 disabled:cursor-not-allowed">
                      Cancelar
                    </Button>
                  </div>
                </div>
              )}

              {/* ── STEP REVISÃO ── */}
              {conferenciaStep === 'revisao' && (
                <div className="p-6 space-y-5">
                  {loadingConferencia ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin"/>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Conferindo com o RepFlow...</p>
                    </div>
                  ) : (
                    <>
                      {erroWizard && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs font-bold text-red-700 flex items-center gap-2">
                          <XCircle size={14}/> {erroWizard}
                        </div>
                      )}
                      {avisosExtracao.length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1">
                          {avisosExtracao.map((a, i) => (
                            <p key={i} className="text-xs font-bold text-amber-800 flex items-start gap-2">
                              <AlertTriangle size={13} className="mt-0.5 flex-shrink-0"/> {a}
                            </p>
                          ))}
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className={lblSm}>Período Início *</label>
                          <input type="date" value={periodoInicio} onChange={e => setPeriodoInicio(e.target.value)} className={inpSm}/>
                        </div>
                        <div>
                          <label className={lblSm}>Período Fim *</label>
                          <input type="date" value={periodoFim} onChange={e => setPeriodoFim(e.target.value)} className={inpSm}/>
                        </div>
                      </div>
                      {periodoInicio && periodoFim && periodoInicio > periodoFim && (
                        <p className="text-xs font-bold text-red-600 flex items-center gap-1.5 -mt-2">
                          <AlertTriangle size={12}/> O período de início não pode ser posterior ao período de fim.
                        </p>
                      )}
                      <div className="overflow-x-auto rounded-xl border border-slate-200">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                              <th className="px-3 py-2 text-left font-black text-slate-500 uppercase tracking-wide">Cliente</th>
                              <th className="px-3 py-2 text-left font-black text-slate-500 uppercase tracking-wide">NF</th>
                              <th className="px-3 py-2 text-left font-black text-slate-500 uppercase tracking-wide">Parcela</th>
                              <th className="px-3 py-2 text-left font-black text-slate-500 uppercase tracking-wide">Valor Comissão</th>
                              <th className="px-3 py-2 w-10"/>
                            </tr>
                          </thead>
                          <tbody>
                            {itensRevisao.map((it, i) => (
                              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                                <td className="px-2 py-1.5">
                                  <input value={it.cliente || ''} onChange={e => handleEditarItemRevisao(i, 'cliente', e.target.value)}
                                    className="w-full border border-slate-200 rounded-lg bg-white px-2 h-8 text-xs font-semibold outline-none focus:border-[#0A3D73]"/>
                                </td>
                                <td className="px-2 py-1.5">
                                  <input value={it.numero_nf || ''} onChange={e => handleEditarItemRevisao(i, 'numero_nf', e.target.value)}
                                    className="w-full border border-slate-200 rounded-lg bg-white px-2 h-8 text-xs font-mono font-bold outline-none focus:border-[#0A3D73]"/>
                                </td>
                                <td className="px-2 py-1.5">
                                  <input type="number" min="1" step="1" value={it.parcela ?? ''} onChange={e => handleEditarItemRevisao(i, 'parcela', e.target.value)}
                                    className="w-16 border border-slate-200 rounded-lg bg-white px-2 h-8 text-xs font-bold outline-none focus:border-[#0A3D73]"/>
                                </td>
                                <td className="px-2 py-1.5">
                                  <input value={it.valor_comissao ?? ''} onChange={e => handleEditarItemRevisao(i, 'valor_comissao', e.target.value)}
                                    className="w-28 border border-slate-200 rounded-lg bg-white px-2 h-8 text-xs font-mono font-bold outline-none focus:border-[#0A3D73]"/>
                                </td>
                                <td className="px-2 py-1.5 text-center">
                                  <button type="button" onClick={() => handleRemoverItemRevisao(i)}
                                    className="text-red-500 hover:text-red-700 transition-colors" title="Remover linha">
                                    <Trash2 size={14}/>
                                  </button>
                                </td>
                              </tr>
                            ))}
                            {itensRevisao.length === 0 && (
                              <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-400 font-semibold">Nenhum item — envie outro arquivo ou cancele</td></tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="flex gap-3 pt-2 border-t border-slate-100">
                        <Button type="button" onClick={handleFecharConferenciaWizard}
                          className="rounded-xl text-xs font-black uppercase h-10 flex-1 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
                          Cancelar
                        </Button>
                        <Button type="button" onClick={handleConferirFabrica}
                          disabled={!periodoInicio || !periodoFim || itensRevisao.length === 0}
                          className="bg-[#0A3D73] hover:bg-[#082D54] text-white rounded-xl text-xs font-black uppercase h-10 flex-1 disabled:opacity-60 disabled:cursor-not-allowed">
                          Conferir no RepFlow
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── STEP CONFERÊNCIA (RESULTADO DA SIMULAÇÃO) ── */}
              {conferenciaStep === 'conferencia' && conferenciaResultado && (
                <div className="p-6 space-y-4">
                  {loadingBaixa ? (
                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin"/>
                      <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Confirmando baixa...</p>
                    </div>
                  ) : (
                    <>
                      {erroWizard && (
                        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs font-bold text-red-700 flex items-center gap-2">
                          <XCircle size={14}/> {erroWizard}
                        </div>
                      )}
                      {conferenciaResultado.aviso_reenvio && (
                        <div className="bg-amber-100 border border-amber-300 rounded-xl p-3 text-xs font-black text-amber-900 flex items-center gap-2">
                          <AlertTriangle size={14}/> {conferenciaResultado.aviso_reenvio}
                        </div>
                      )}
                      {conferenciaResultado.ja_baixados?.length > 0 && (
                        <p className="text-xs font-semibold text-slate-500 flex items-center gap-2">
                          <CheckCircle2 size={13} className="text-emerald-500"/>
                          {conferenciaResultado.ja_baixados.length} item(ns) já estavam pagos — não serão reprocessados.
                        </p>
                      )}
                      {conferenciaResultado.avisos?.length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-1">
                          {conferenciaResultado.avisos.map((a, i) => (
                            <p key={i} className="text-xs font-bold text-amber-800 flex items-start gap-2">
                              <AlertTriangle size={13} className="mt-0.5 flex-shrink-0"/> {a}
                            </p>
                          ))}
                        </div>
                      )}
                      {conferenciaResultado.avisos_data_invalida?.length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs font-bold text-amber-800 flex items-start gap-2">
                          <AlertTriangle size={13} className="mt-0.5 flex-shrink-0"/>
                          {conferenciaResultado.avisos_data_invalida.length} vencimento(s) pendente(s) da representada com data de vencimento ausente/inválida — não puderam ser conferidos automaticamente.
                        </div>
                      )}

                      <BlocoResultado
                        titulo="Elegíveis para Baixa" Icone={CheckCircle2}
                        cor={{ borda: 'border-emerald-200', fundo: 'bg-emerald-50', icone: 'text-emerald-600', texto: 'text-emerald-800', badge: 'bg-emerald-200 text-emerald-800' }}
                        itens={conferenciaResultado.elegiveis_baixa || []}
                        colunas={[
                          { key: 'numero_nf', label: 'NF' },
                          { key: 'parcela', label: 'Parcela' },
                          { key: 'valor_fabrica', label: 'Valor Fábrica', render: it => `R$ ${fmt(it.valor_fabrica)}` },
                          { key: 'valor_repflow', label: 'Valor RepFlow', render: it => `R$ ${fmt(it.valor_repflow)}` },
                        ]}
                        aberto={blocosAbertos.elegiveis} onToggle={() => toggleBloco('elegiveis')}
                        vazio="Nenhum item elegível para baixa."
                      />
                      <BlocoResultado
                        titulo="Divergências" Icone={AlertTriangle}
                        cor={{ borda: 'border-amber-200', fundo: 'bg-amber-50', icone: 'text-amber-600', texto: 'text-amber-800', badge: 'bg-amber-200 text-amber-800' }}
                        itens={conferenciaResultado.divergencias || []}
                        colunas={[
                          { key: 'numero_nf', label: 'NF' },
                          { key: 'parcela', label: 'Parcela' },
                          { key: 'valor_fabrica', label: 'Valor Fábrica', render: it => `R$ ${fmt(it.valor_fabrica)}` },
                          { key: 'valor_repflow', label: 'Valor RepFlow', render: it => it.valor_repflow != null ? `R$ ${fmt(it.valor_repflow)}` : '—' },
                          { key: 'diferenca_percent', label: 'Diferença %', render: it => it.diferenca_percent != null ? `${it.diferenca_percent}%` : '—' },
                          { key: 'motivo', label: 'Motivo' },
                        ]}
                        aberto={blocosAbertos.divergencias} onToggle={() => toggleBloco('divergencias')}
                        vazio="Nenhuma divergência encontrada."
                      />
                      <BlocoResultado
                        titulo="Não Encontrados no RepFlow" Icone={XCircle}
                        cor={{ borda: 'border-red-200', fundo: 'bg-red-50', icone: 'text-red-600', texto: 'text-red-800', badge: 'bg-red-200 text-red-800' }}
                        itens={conferenciaResultado.nao_encontrados_repflow || []}
                        colunas={[
                          { key: 'numero_nf', label: 'NF' },
                          { key: 'parcela', label: 'Parcela' },
                          { key: 'valor_fabrica', label: 'Valor Fábrica', render: it => `R$ ${fmt(it.valor_fabrica)}` },
                        ]}
                        aberto={blocosAbertos.naoRepflow} onToggle={() => toggleBloco('naoRepflow')}
                        vazio="Todos os itens do relatório foram encontrados no RepFlow."
                      />
                      <BlocoResultado
                        titulo="Não Encontrados no Relatório" Icone={XCircle}
                        cor={{ borda: 'border-red-200', fundo: 'bg-red-50', icone: 'text-red-600', texto: 'text-red-800', badge: 'bg-red-200 text-red-800' }}
                        itens={conferenciaResultado.nao_encontrados_relatorio || []}
                        colunas={[
                          { key: 'numero_nf', label: 'NF' },
                          { key: 'parcela', label: 'Parcela' },
                          { key: 'valor_repflow', label: 'Valor RepFlow', render: it => `R$ ${fmt(it.valor_repflow)}` },
                        ]}
                        aberto={blocosAbertos.naoRelatorio} onToggle={() => toggleBloco('naoRelatorio')}
                        vazio="Nenhum pendente do período ficou de fora do relatório."
                      />

                      <div className="grid grid-cols-2 gap-3 bg-slate-50 border border-slate-200 rounded-xl p-4">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Total Elegível — Fábrica</p>
                          <p className="text-lg font-black font-mono text-slate-700">R$ {fmt(conferenciaResultado.total_elegivel_fabrica)}</p>
                        </div>
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Total Elegível — RepFlow</p>
                          <p className="text-lg font-black font-mono text-slate-700">R$ {fmt(conferenciaResultado.total_elegivel_repflow)}</p>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <Button type="button" onClick={handleExportarConferenciaExcel} disabled={loadingExportConferenciaExcel}
                          className="flex items-center gap-2 bg-[#166534] hover:bg-[#14532D] text-white rounded-xl text-xs font-black uppercase h-9 px-4 disabled:opacity-60 disabled:cursor-not-allowed">
                          <FileDown size={13}/> {loadingExportConferenciaExcel ? 'Gerando...' : 'Exportar Excel'}
                        </Button>
                      </div>

                      <div className="flex gap-3 pt-2 border-t border-slate-100">
                        <Button type="button" onClick={handleFecharConferenciaWizard}
                          className="rounded-xl text-xs font-black uppercase h-10 flex-1 border border-slate-200 bg-white text-slate-600 hover:bg-slate-50">
                          Cancelar
                        </Button>
                        <Button type="button" onClick={handleConfirmarBaixa}
                          disabled={(conferenciaResultado.elegiveis_baixa?.length || 0) === 0}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-black uppercase h-10 flex-1 disabled:opacity-60 disabled:cursor-not-allowed">
                          <CheckCircle2 size={13} className="mr-1.5"/>
                          Confirmar baixa de {conferenciaResultado.elegiveis_baixa?.length || 0} comissões
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ── STEP RESULTADO (BAIXA CONFIRMADA) ── */}
              {conferenciaStep === 'resultado' && baixaResultado && (
                <div className="p-6 space-y-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-center">
                      <p className="text-3xl font-black text-emerald-700">{baixaResultado.baixados?.length ?? 0}</p>
                      <p className="text-xs font-bold uppercase tracking-wide text-emerald-600 mt-1">Comissões baixadas</p>
                    </div>
                    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-center">
                      <p className="text-3xl font-black text-slate-700">R$ {fmt(baixaResultado.total_baixado_repflow)}</p>
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mt-1">Total baixado (RepFlow)</p>
                    </div>
                  </div>
                  <p className="text-xs font-semibold text-slate-500 text-center">
                    Total Fábrica correspondente: R$ {fmt(baixaResultado.total_baixado_fabrica)}
                  </p>
                  <div className="flex justify-end pt-2 border-t border-slate-100">
                    <Button type="button" onClick={handleFecharConferenciaWizard}
                      className="bg-[#0A3D73] hover:bg-[#082D54] text-white rounded-xl text-xs font-black uppercase h-10 px-8">
                      Fechar
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Comissoes;